import dayjs from 'dayjs';
import { Button, Popover, Tag } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { EllipsisOutlined } from '@ant-design/icons';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TMyMeeting } from '@/common/basis/types/database/tmy-meeting';
import { TField } from '@/common/basis/types/tfield';
import {
  setBookingMeetingType,
  setBookingRoomId,
  setCancelRoomId,
  setDetailRoomId,
  setIsOpenBookingDetail,
  setIsOpenBookingMeeting,
  setIsOpenCannelMeeting,
} from '@/common/store/booking/slice';
import { usePersonUserId } from '@/common/hooks/person-user-id';
import { setMeetingShareData } from '@/common/store/popup/slice';
import { useMeetingType } from '@/common/hooks/meeting-type';
import { useMeetingBrightPassword } from '@/common/hooks/meeting-bright-password';
import { joinParameters } from '@/common/utils/uri';
import CheckBeforeJoin from '@/components/HOC/CheckBeforeJoin';

function App(props: {
  index: number;
  list: TMyMeeting.CreateItem[];
  setIsOpenRepeatMeeting: (isOpenRepeatMeeting: boolean) => void;
  setCurrentCreateIndex: (currentCreateIndex: number) => void;
}) {
  const dispatch = useDispatch();
  const index = props.index;
  const list = props.list;

  const item = useMemo(() => list[index], [list, index]);
  const roomName = useMemo(() => item?.roomName ?? '', [item?.roomName]);
  const startTime = useMemo(() => item?.startTime ?? 0, [item?.startTime]);
  const duration = useMemo(() => item?.duration ?? 0, [item?.duration]);
  const repeatType = useMemo(() => item?.repeatType, [item?.repeatType]);
  const roomId = useMemo(() => item?.roomId ?? '', [item?.roomId]);
  const defaultHost = useMemo(() => item?.defaultHost ?? '', [item?.defaultHost]);
  const meetingType = useMemo(() => item?.meetingType ?? TField.MEETING_TYPE_BOOKING, [item?.meetingType]);
  const { isMeetingLive } = useMeetingType(meetingType);
  const { t } = useTranslation();
  const setIsOpenRepeatMeeting = props.setIsOpenRepeatMeeting;
  const setCurrentCreateIndex = props.setCurrentCreateIndex;
  const isRepeatMeeting = useMemo(() => repeatType && repeatType !== TField.MEET_REPEAT_TYPE_NONE, [repeatType]);
  const repeatStr = useMemo(() => (isRepeatMeeting ? t('cycle') : ''), [isRepeatMeeting]);
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [isShowHandMenu, setIsShowHandMenu] = useState(false);
  const [isShowMoreMenu, setIsShowMoreMenu] = useState(false);
  const isHideMoreMenu = useMemo(() => !isShowMoreMenu, [isShowMoreMenu]);
  const { currentUserId } = usePersonUserId();
  const isMyOrganizer = useMemo(() => currentUserId === defaultHost, [currentUserId, defaultHost]); // 是否为会议组织者
  const isRenderMoreMenu = useMemo(() => isMyOrganizer || isRepeatMeeting, [isMyOrganizer, isRepeatMeeting]);

  const password = useMemo(() => item?.password || '', [item?.password]);
  const nonce = useMemo(() => item?.nonce || '', [item?.nonce]);
  const webUrl = useMemo(() => item?.webUrl || '', [item?.webUrl]);
  const liveUrl = useMemo(() => (isMeetingLive ? item?.liveUrl || '' : ''), [item?.liveUrl, isMeetingLive]);
  const { brightPassword } = useMeetingBrightPassword(password, nonce);

  useEffect(() => {
    if (isHideMoreMenu && !isMouseOver) {
      setIsShowHandMenu(false);
    }
  }, [isHideMoreMenu, isMouseOver]);

  return (
    <>
      <div
        className='mb-3 cursor-pointer'
        onMouseEnter={() => {
          setIsShowHandMenu(true);
          setIsMouseOver(true);
        }}
        onMouseLeave={() => {
          !isShowMoreMenu && setIsShowHandMenu(false);
          setIsMouseOver(false);
        }}
      >
        <div className='relative flex h-14 items-center justify-between rounded-md bg-[rgba(44,125,240,0.06)] py-[7px]'>
          <div
            className='flex flex-1 items-center justify-start'
            onClick={() => {
              dispatch(setIsOpenBookingDetail(true));
              dispatch(setDetailRoomId(roomId));
            }}
          >
            <div className='h-8 overflow-hidden px-3'>
              {isMeetingLive ? (
                <img className='h-8 w-8' src={require('@/common/basis/assets/live_create_icon.png')} />
              ) : (
                <img className='h-8 w-8' src={require('@/common/basis/assets/vide_create_icon.png')} />
              )}
            </div>
            <div className='flex-1'>
              <div className='flex h-5 items-center justify-start'>
                <div
                  className='line-clamp-1 overflow-hidden break-words text-[15px]'
                  style={{ wordBreak: 'break-word' }}
                >
                  <span>{roomName}</span>
                </div>
                {repeatStr ? (
                  <Tag className='ml-2 flex h-[18px] items-center justify-center px-1 text-[12px]' color='blue'>
                    {repeatStr}
                  </Tag>
                ) : (
                  <></>
                )}
              </div>
              <div className='mt-1 flex items-center justify-start text-[12px] text-[#aaa] transition-all'>
                <span className='flex items-center justify-center'>
                  {dayjs(startTime).format(`${t('YYYYMMDDFormat')} HH:mm`)}
                  <span className='mx-1'>-</span>
                  {duration > 24 * 60 * 60 * 1000
                    ? dayjs(startTime + duration).format(`${t('YYYYMMDDFormat')} HH:mm`)
                    : dayjs(startTime + duration).format('HH:mm')}
                </span>
                <span className='mx-1 h-[10px] w-[1px] overflow-hidden bg-[rgba(19,20,21,.22)]' />
                <span>{roomId}</span>
              </div>
            </div>
          </div>
          <div
            className={`flex items-center justify-end transition-all duration-500 ${
              isShowHandMenu ? 'opacity-100' : 'relative opacity-0'
            }`}
          >
            <div className='mr-3'>
              <Button
                ghost
                onClick={() => dispatch(setMeetingShareData({ roomId, roomName, brightPassword, webUrl, liveUrl }))}
                type='primary'
              >
                <span className='text-[12px]'>{t('invite')}</span>
              </Button>
            </div>
            <div className={`relative ${isRenderMoreMenu ? 'mr-1' : 'mr-3'}`}>
              <CheckBeforeJoin
                callback={(success) => {
                  success && window.open(joinParameters(webUrl, { showSchema: 0 }));
                }}
                roomId={roomId}
              >
                <Button type='primary'>
                  <span className='text-[12px]'>{t('enrollMeeting')}</span>
                </Button>
              </CheckBeforeJoin>
            </div>
            {isRenderMoreMenu ? (
              <div>
                <Popover
                  content={
                    <div className='px-2'>
                      {isRepeatMeeting ? (
                        <div className='cursor-pointer py-2 hover:text-[#002cda]'>
                          <span
                            onClick={() => {
                              setIsOpenRepeatMeeting(true);
                              setIsShowMoreMenu(false);
                              setCurrentCreateIndex(index);
                            }}
                          >
                            {t('otherRepeat')}
                          </span>
                        </div>
                      ) : (
                        <></>
                      )}
                      {isMyOrganizer ? (
                        <>
                          <div
                            className='cursor-pointer py-2 hover:text-[#002cda]'
                            onClick={() => {
                              dispatch(setIsOpenBookingMeeting(true));
                              dispatch(setBookingMeetingType(meetingType));
                              dispatch(setBookingRoomId(roomId));
                              setIsShowMoreMenu(false);
                            }}
                          >
                            <span>{isRepeatMeeting ? t('modifyMeetingInfo') : t('modifyThisMeeting')}</span>
                          </div>
                          <div
                            className='cursor-pointer py-2 hover:text-[#002cda]'
                            onClick={() => {
                              dispatch(setIsOpenCannelMeeting(true));
                              dispatch(setCancelRoomId(roomId));
                              setIsShowMoreMenu(false);
                            }}
                          >
                            <span>{t('cancelMeeting')}</span>
                          </div>
                        </>
                      ) : (
                        <></>
                      )}
                    </div>
                  }
                  onOpenChange={(open) => setIsShowMoreMenu(open)}
                  open={isShowMoreMenu}
                  placement='bottomLeft'
                  trigger='click'
                >
                  <div className='relative flex items-center justify-center px-2'>
                    <EllipsisOutlined className='text-[18px]' />
                  </div>
                </Popover>
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default App;
